import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Avatar, AvatarGroup, Box, Button, IconButton, Tooltip, Typography } from "@mui/material";

import { User } from "../../../../../types/types";

import { useUserDataFormatter } from "../../../../User/hooks/useUserDataFormatter";

import { ChallengePost } from "../../../types/types";
import { usePostFormatter } from "../../../hooks/usePostFormatter";
import {
  ChallengeLockedIcon,
  ChallengePlayButtonIcon,
  JoinChallengeButtonIcon,
} from "../../../assets/icons/post-icons";
import { ChallengeStatusEnum } from "../../../enums/post.enum";

const ChallengePostCard = ({
  post,
  handlePlayClick,
}: {
  post: ChallengePost;
  handlePlayClick: (post: ChallengePost) => void;
}) => {
  const { t } = useTranslation("feed", { keyPrefix: "postCard.challengePostCard" });

  const { postMedia, winner, authorFullName, authorMedia, authorFirstTwoChr, authorLink } = usePostFormatter(post);

  const DoesWinnerExist = post.challenge.winner;
  return (
    <Box sx={styles.wrapper}>
      <Box sx={styles.container}>
        <Box sx={styles.header}>
          {DoesWinnerExist && (
            <Box sx={styles.winnerContainer} component={Link} to={winner.profileLink}>
              <Avatar src={winner.avatar} sx={styles.winnerAvatar}>
                {winner.firstTwoChr}
              </Avatar>
              <Box sx={styles.winnerInfoContainer}>
                <Typography sx={styles.winnerName}>{winner.name}</Typography>
                <Typography sx={styles.winnerLikesCount}>
                  {t("likes", { count: post.challenge.winnerLikesCount })}
                </Typography>
              </Box>
            </Box>
          )}
          <Box sx={styles.daysLeftContainer}>
            {post.challenge.status === ChallengeStatusEnum.STARTED && (
              <Typography sx={styles.daysLeftText}>{t("daysLeft", { days: post.challenge.daysRemaining })}</Typography>
            )}
            <Box sx={styles.lockedIconContainer}>
              <ChallengeLockedIcon />
            </Box>
          </Box>
        </Box>
        <Box sx={styles.content}>
          <Box sx={styles.challengeContentContainer}>
            <Typography sx={styles.title}>{post.challenge.name}</Typography>
            <Typography sx={styles.description}>{post.challenge.description}</Typography>
          </Box>
          <Box sx={styles.detailsContainer}>
            <Box sx={styles.organizerContainer}>
              <Typography sx={styles.organizerText}>{t("organizer")}</Typography>
              <Box sx={styles.organizerInfoContainer} component={Link} to={authorLink}>
                <Avatar src={authorMedia} sx={styles.organizerAvatar}>
                  {authorFirstTwoChr}
                </Avatar>
                <Typography sx={styles.organizerName}>{authorFullName}</Typography>
              </Box>
            </Box>
            {post?.challenge?.participants?.length && (
              <Box sx={styles.participantsContainer}>
                <Typography sx={styles.participantsText}>
                  {t("participantsNumber", { number: post.challenge.participants.length })}
                </Typography>
                <AvatarGroup total={post.challenge.participants.length} max={4}>
                  {post.challenge.participants.map((participant) => (
                    <ParticipantAvatar key={participant.id} participant={participant} />
                  ))}
                </AvatarGroup>
              </Box>
            )}
          </Box>
          <Tooltip
            title={t("joinChallengeThroughGoalngoMobileApp")}
            placement="bottom"
            slotProps={{
              tooltip: {
                sx: styles.tooltip,
              },
            }}
          >
            <span>
              <Button sx={styles.joinChallengeButton} disabled fullWidth>
                <JoinChallengeButtonIcon />
                <Typography sx={styles.joinChallengeText}>{t("joinChallenge")}</Typography>
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>
      <Box component="img" src={postMedia?.[0]?.thumbnail} alt="Challenge thumbnail" sx={styles.poster} />
      <Box sx={styles.overlay} />

      <IconButton sx={styles.playButton} onClick={() => handlePlayClick(post)} disabled>
        <ChallengePlayButtonIcon />
      </IconButton>
    </Box>
  );
};

export default ChallengePostCard;

const ParticipantAvatar = ({ participant }: { participant: User }) => {
  const { firstTwoChr, media } = useUserDataFormatter(participant);
  return (
    <Avatar src={media} sx={styles.participantAvatar}>
      {firstTwoChr}
    </Avatar>
  );
};

const styles = {
  wrapper: {
    width: "calc(100% - 32px)",
    height: "544px",
    borderRadius: "10px",
    boxShadow: "0 4px 32px 0 rgba(23, 26, 28, 0.10)",
    overflow: "hidden",
    position: "relative",
    mx: "auto",
    padding: "16px 16px 20px",
  },
  container: {
    display: "flex",
    flexDirection: "column",
    zIndex: 10,
    position: "relative",
    height: "100%",
  },
  poster: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1,
  },
  overlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "linear-gradient(180deg, rgba(0, 0, 0, 0.18) 0%, rgba(0, 0, 0, 0.60) 100%)",
    zIndex: 2,
    pointerEvents: "none",
  },
  video: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  playButton: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    padding: 0,
    bgcolor: "rgba(0, 0, 0, 0.5)",
    borderRadius: "50%",
    border: "1px solid #fff",
    width: "56px",
    height: "56px",
    zIndex: 100,
    "&:hover": {
      bgcolor: "rgba(0, 0, 0, 0.7)",
    },
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  winnerContainer: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    textDecoration: "none",
  },
  winnerAvatar: {
    width: "42px",
    height: "42px",
    padding: "2px",
    border: "1px solid #fff",
    borderRadius: "50%",
  },
  winnerInfoContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  winnerName: {
    color: "var(--White, #FFF)",
    textShadow: "0 0 1px rgba(0, 0, 0, 0.16)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "normal",
  },
  winnerLikesCount: {
    color: "var(--White, #FFF)",
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "normal",
  },
  daysLeftContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  daysLeftText: {
    lineHeight: "32px",
    fontFamily: "Montserrat",
    color: "var(--White, #FFF)",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 700,
    borderRadius: "4px",
    background: "rgba(243, 244, 243, 0.20)",
    backdropFilter: "blur(12px)",
    padding: "4px 8px 4px 16px",
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      left: "8px",
      top: "50%",
      width: "6px",
      height: "6px",
      background: "var(--Green---100, #008B07)",
      borderRadius: "50%",
      transform: "translate(-50%, -50%)",
      zIndex: 1,
    },
  },
  lockedIconContainer: {
    borderRadius: "4px",
    background: "rgba(243, 244, 243, 0.20)",
    backdropFilter: "blur(12px)",
    padding: "4px 8px",
  },
  content: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    gap: "6px",
  },
  challengeContentContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  title: {
    color: "var(--White, #FFF)",
    fontFamily: "Montserrat",
    fontSize: "24px",
    fontStyle: "normal",
    fontWeight: 800,
    lineHeight: "normal",
  },
  description: {
    color: "var(--White, #FFF)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "normal",
    lineClamp: 2,
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    "-webkit-line-clamp": 2,
    "-webkit-box-orient": "vertical",
  },
  detailsContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    mb: "14px",
  },
  organizerContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  organizerText: {
    color: "var(--White, #FFF)",
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "normal",
  },
  organizerInfoContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    textDecoration: "none",
  },
  organizerAvatar: {
    width: "24px",
    height: "24px",
    color: "var(--White, #FFF)",
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "normal",
  },
  organizerName: {
    color: "var(--White, #FFF)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "normal",
  },
  participantsContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
  },
  participantsText: {
    color: "var(--White, #FFF)",
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "normal",
  },
  participantAvatar: {
    width: "24px",
    height: "24px",
    color: "var(--White, #FFF)",
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontStyle: "normal",
    overflow: "hidden",
  },
  joinChallengeButton: {
    display: "flex",
    padding: "12px 40px",
    justifyContent: "center",
    alignItems: "center",
    gap: "10px",
    borderRadius: "8px",
    background: "var(--White, #FFF)",
  },
  joinChallengeText: {
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    textAlign: "center",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "24px",
  },
  tooltip: {
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "normal",
    whiteSpace: "nowrap",
    maxWidth: "none",
  },
};
